import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mono = "'Space Mono', monospace";
const serifDisplay = "'Libre Caslon Display', serif";
const overlay = { position: 'fixed', inset: 0, background: 'color-mix(in oklch, var(--ink) 55%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 };
const box = { background: 'var(--paper)', color: 'var(--ink)', border: '1px solid var(--line)', boxShadow: '8px 9px 0 color-mix(in oklch, var(--line) 35%, transparent)', padding: '30px 32px', maxWidth: 520, width: '100%', maxHeight: '85vh', overflowY: 'auto' };
const monoBtn = (bg, fg) => ({ cursor: 'pointer', border: '1px solid var(--line)', background: bg, color: fg, fontFamily: mono, fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', padding: '9px 18px' });
const inputStyle = { width: '100%', boxSizing: 'border-box', border: '1px solid var(--line)', background: 'var(--bg)', padding: '9px 12px', fontSize: 14, color: 'var(--ink)', fontFamily: 'inherit' };

export default function AddonReserveButton({ addon, user, getAuthHeader, onDone, fg = 'var(--ink)', category = null }) {
    const [subscribing, setSubscribing] = useState(false);
    const [promoOpen, setPromoOpen] = useState(false);
    const [ticketOpen, setTicketOpen] = useState(false);
    const [qty, setQty] = useState(1);
    const [guests, setGuests] = useState([]);
    const [preview, setPreview] = useState(null);
    const [ticketLoading, setTicketLoading] = useState(false);

    const catId = category?.id || null;
    const yourPrice = Number((category ? category.your_price : (addon?.your_price ?? addon?.base_price)) ?? 0);
    const memberPrice = Number((category ? category.member_price : addon?.base_price) ?? 0);
    const savings = memberPrice < yourPrice ? yourPrice - memberPrice : 0;
    const isPaid = yourPrice > 0;
    const mode = addon?.payment_mode || 'one_time';

    useEffect(() => {
        if (!ticketOpen) return;
        if (qty === 1) {
            setPreview({ total: yourPrice, quantity: 1, breakdown: { guests: [] } });
            return;
        }
        if (!guests.every(g => g.name && g.email)) return;
        const t = setTimeout(async () => {
            try {
                const res = await axios.post(`${API}/addons/${addon.id}/preview-group`, { quantity: qty, guests, ticket_category_id: catId }, { headers: getAuthHeader() });
                setPreview(res.data);
            } catch { /* surfaced at checkout */ }
        }, 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qty, guests, ticketOpen]);

    const done = () => { onDone?.(); window.dispatchEvent(new Event('payments-updated')); };

    const proceedCheckout = async () => {
        setSubscribing(true);
        try {
            if (!isPaid) {
                const res = await axios.post(`${API}/addons/${addon.id}/subscribe`, { ticket_category_id: catId }, { headers: getAuthHeader() });
                alert(res.data.message);
                done();
                return;
            }
            if (mode === 'one_time') {
                const orderRes = await axios.post(`${API}/addons/${addon.id}/create-order`, { ticket_category_id: catId }, { headers: getAuthHeader() });
                const { order_id, amount, currency, key_id, addon_name } = orderRes.data;
                const rzp = new window.Razorpay({
                    key: key_id, amount, currency, order_id,
                    name: 'The Hyd Library', description: category ? `${addon_name} — ${category.name}` : addon_name,
                    handler: async (response) => {
                        try {
                            const v = await axios.post(`${API}/addons/${addon.id}/verify-payment`, {
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_signature: response.razorpay_signature,
                            }, { headers: getAuthHeader() });
                            alert(v.data.message || 'Booked!');
                            done();
                        } catch (err) {
                            alert(err.response?.data?.detail || 'Payment verification failed. Please contact support.');
                        }
                    },
                    prefill: { name: user?.name, email: user?.email, contact: user?.phone || '' },
                    theme: { color: '#31435f' },
                    modal: { ondismiss: () => setSubscribing(false), escape: true, animation: true },
                });
                rzp.on('payment.failed', (resp) => { alert(`Payment failed: ${resp.error?.description || 'Unknown error'}.`); setSubscribing(false); });
                rzp.open();
                return;
            }
            if (mode === 'autopay') {
                const subRes = await axios.post(`${API}/addons/${addon.id}/create-autopay`, {}, { headers: getAuthHeader() });
                const { subscription_id, key_id, addon_name } = subRes.data;
                const rzp = new window.Razorpay({
                    key: key_id, subscription_id,
                    name: 'The Hyd Library', description: `${addon_name} (autopay)`,
                    handler: async (response) => {
                        try {
                            const v = await axios.post(`${API}/addons/${addon.id}/verify-autopay`, {
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_subscription_id: response.razorpay_subscription_id || subscription_id,
                                razorpay_signature: response.razorpay_signature,
                            }, { headers: getAuthHeader() });
                            alert(v.data.message || 'Subscribed!');
                            done();
                        } catch (err) {
                            alert(err.response?.data?.detail || 'Autopay activation failed. Please contact support.');
                        }
                    },
                    prefill: { name: user?.name, email: user?.email, contact: user?.phone || '' },
                    theme: { color: '#31435f' },
                    modal: { ondismiss: () => setSubscribing(false), escape: true, animation: true },
                });
                rzp.on('payment.failed', (resp) => { alert(`Payment failed: ${resp.error?.description || 'Unknown error'}.`); setSubscribing(false); });
                rzp.open();
            }
        } catch (err) {
            alert(err.response?.data?.detail || 'Failed to reserve');
        } finally {
            if (!isPaid) setSubscribing(false);
        }
    };

    const handleClick = () => {
        if (addon.your_audience === 'non_member' && savings > 0) { setPromoOpen(true); return; }
        if (mode === 'one_time' && isPaid && addon.your_audience !== 'blocked') {
            setTicketOpen(true);
            setQty(1);
            setGuests([]);
            setPreview({ total: yourPrice, quantity: 1, breakdown: { guests: [] } });
            return;
        }
        proceedCheckout();
    };

    const updateQty = (n) => {
        const caps = [addon.spots_remaining, category?.remaining].filter(v => v != null);
        const maxAllowed = caps.length ? Math.max(1, Math.min(...caps)) : 20;
        const q = Math.max(1, Math.min(maxAllowed, n));
        setQty(q);
        setGuests(prev => {
            const next = [...prev];
            while (next.length < q - 1) next.push({ name: '', email: '', phone: '', is_member: false });
            while (next.length > q - 1) next.pop();
            return next;
        });
    };

    const ticketCheckout = async () => {
        if (qty === 1) { setTicketOpen(false); await proceedCheckout(); return; }
        for (let i = 0; i < guests.length; i++) {
            if (!guests[i].name.trim() || !guests[i].email.trim()) { alert(`Guest #${i + 1}: name and email are required.`); return; }
        }
        setTicketLoading(true);
        try {
            const orderRes = await axios.post(`${API}/addons/${addon.id}/create-order-group`, { quantity: qty, guests, ticket_category_id: catId }, { headers: getAuthHeader() });
            const { order_id, amount, currency, key_id, addon_name } = orderRes.data;
            const rzp = new window.Razorpay({
                key: key_id, amount, currency, order_id,
                name: 'The Hyd Library', description: `${addon_name}${category ? ` — ${category.name}` : ''} — ${qty} tickets`,
                handler: async (response) => {
                    try {
                        const v = await axios.post(`${API}/addons/${addon.id}/verify-payment-group`, {
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_signature: response.razorpay_signature,
                        }, { headers: getAuthHeader() });
                        alert(v.data.message || 'Booking confirmed!');
                        setTicketOpen(false);
                        done();
                    } catch (err) {
                        alert(err.response?.data?.detail || 'Payment verification failed. Please contact support.');
                    }
                },
                prefill: { name: user?.name, email: user?.email, contact: user?.phone || '' },
                theme: { color: '#31435f' },
                modal: { ondismiss: () => setTicketLoading(false), escape: true, animation: true },
            });
            rzp.on('payment.failed', (resp) => { alert(`Payment failed: ${resp.error?.description || 'Unknown error'}.`); setTicketLoading(false); });
            rzp.open();
        } catch (err) {
            alert(err.response?.data?.detail || 'Failed to create order');
        } finally {
            setTicketLoading(false);
        }
    };

    const catBlocked = category ? (category.sold_out || category.expired) : false;
    const soldOut = addon.spots_remaining === 0 || catBlocked;
    const baseLabel = category
        ? `${category.name} — ${isPaid ? `₹${yourPrice}` : 'Free'}`
        : (isPaid ? `Get tickets — ₹${yourPrice}` : 'Reserve a spot');
    const soldOutLabel = category ? `${category.name} — ${category.expired ? 'ended' : 'sold out'}` : 'Fully booked';
    const label = subscribing ? 'Opening…' : soldOut ? soldOutLabel : baseLabel;

    return (
        <>
            <button
                onClick={handleClick}
                disabled={subscribing || soldOut}
                data-testid={category ? `addon-reserve-${addon.id}-${category.id}` : `addon-reserve-${addon.id}`}
                style={{ display: 'inline-block', marginTop: category ? 8 : 14, cursor: soldOut ? 'default' : 'pointer', background: 'transparent', border: `1px solid ${fg}`, color: fg, fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', padding: '7px 14px', opacity: (subscribing || soldOut) ? 0.6 : 1 }}
            >
                {label}
            </button>

            {promoOpen && (
                <div style={overlay} onClick={() => setPromoOpen(false)}>
                    <div style={box} onClick={(e) => e.stopPropagation()} data-testid="addon-promo-modal">
                        <h3 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 26, margin: '0 0 10px' }}>Members save ₹{savings} on this</h3>
                        <p style={{ fontSize: 15, lineHeight: 1.6, margin: '0 0 6px' }}>Your price: <strong>₹{yourPrice}</strong> · Member price: <strong>₹{memberPrice}</strong>.</p>
                        <p style={{ fontSize: 14, opacity: 0.75, margin: '0 0 20px' }}>A ₹500/month membership also gets you five books at a time.</p>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <Link to="/signup" style={{ ...monoBtn('var(--ink)', 'var(--paper)'), textDecoration: 'none' }}>Become a member →</Link>
                            <button onClick={() => { setPromoOpen(false); if (mode === 'one_time' && isPaid) { setTicketOpen(true); setQty(1); setGuests([]); setPreview({ total: yourPrice, quantity: 1, breakdown: { guests: [] } }); } else { proceedCheckout(); } }} data-testid="addon-promo-continue" style={monoBtn('var(--paper)', 'var(--ink)')}>
                                Continue — ₹{yourPrice}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {ticketOpen && (
                <div style={overlay} onClick={() => !ticketLoading && setTicketOpen(false)}>
                    <div style={box} onClick={(e) => e.stopPropagation()} data-testid="addon-ticket-modal">
                        <h3 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 26, margin: '0 0 6px' }}>{addon.name}</h3>
                        {category && <p style={{ fontFamily: mono, fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', margin: '0 0 4px' }} data-testid="ticket-category-label">{category.name} · ₹{yourPrice} each</p>}
                        <p style={{ fontFamily: mono, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--a1)', margin: '0 0 18px' }}>How many spots?</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
                            <button onClick={() => updateQty(qty - 1)} style={monoBtn('var(--paper)', 'var(--ink)')} data-testid="ticket-qty-minus">−</button>
                            <span style={{ fontFamily: serifDisplay, fontSize: 26, minWidth: 30, textAlign: 'center' }} data-testid="ticket-qty">{qty}</span>
                            <button onClick={() => updateQty(qty + 1)} style={monoBtn('var(--paper)', 'var(--ink)')} data-testid="ticket-qty-plus">+</button>
                            {(category?.remaining != null || addon.spots_remaining != null) && (
                                <span style={{ fontSize: 13, opacity: 0.7 }}>
                                    {category?.remaining != null ? `${category.remaining} ${category.name} tickets left` : `${addon.spots_remaining} spots left`}
                                </span>
                            )}
                        </div>
                        {qty > 1 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
                                {guests.map((g, i) => (
                                    <div key={i} style={{ border: '1px dashed var(--line)', padding: '12px 14px', background: 'var(--bg)' }}>
                                        <div style={{ fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8 }}>Guest #{i + 1}</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                            <input placeholder="Name *" value={g.name} onChange={(e) => setGuests(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} style={inputStyle} data-testid={`guest-name-${i}`} />
                                            <input placeholder="Email *" value={g.email} onChange={(e) => setGuests(prev => prev.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} style={inputStyle} data-testid={`guest-email-${i}`} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div style={{ borderTop: '1px dashed var(--line)', paddingTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: serifDisplay, fontSize: 22 }}>Total: ₹{preview?.total ?? yourPrice}</span>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button onClick={() => setTicketOpen(false)} disabled={ticketLoading} style={monoBtn('var(--paper)', 'var(--ink)')}>Cancel</button>
                                <button onClick={ticketCheckout} disabled={ticketLoading} data-testid="ticket-checkout-btn" style={{ ...monoBtn('var(--ink)', 'var(--paper)'), opacity: ticketLoading ? 0.7 : 1 }}>
                                    {ticketLoading ? 'Opening…' : 'Pay & reserve'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
